import { useEffect, useRef, useState } from "react";
import { Loader2, Save, Sparkles, Image as ImageIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  adminGetGlimpseSettings,
  adminUpdateGlimpseSettings,
  uploadGlimpseImage,
  resolveMedia,
} from "@/api/client";

const inputCls =
  "w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 placeholder:text-amber-100/30 outline-none focus:border-amber-300/50";

// "A Glimpse of Śrīmad-Bhāgavatam" entry card — editable from the home page too.
// Reads/writes the shared glimpse settings (entry title/meta/cta + entry image).
export const GlimpseEntryPanel = () => {
  const fileRef = useRef(null);
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);

  useEffect(() => {
    adminGetGlimpseSettings().then(setS).catch(() => {});
  }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const onImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgBusy(true);
    try {
      const { path } = await uploadGlimpseImage(file);
      set("entry_image", path);
      toast.success("Entry image uploaded (remember to Save)");
    } catch {
      toast.error("Upload failed (max 10MB · jpg/png/webp)");
    } finally {
      setImgBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await adminUpdateGlimpseSettings(s);
      setS(updated);
      toast.success("Glimpse entry saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return null;

  return (
    <div
      data-testid="glimpse-entry-panel"
      className="mb-8 rounded-2xl border border-amber-300/12 bg-[#0d1226]/70 p-5"
    >
      <div className="mb-4 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-amber-300" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-amber-100/80">
          &ldquo;A Glimpse of Śrīmad-Bhāgavatam&rdquo; entry card
        </h2>
        <button
          data-testid="glimpse-entry-save"
          onClick={save}
          disabled={saving}
          className="ml-auto inline-flex items-center gap-2 rounded-full bg-amber-400 px-4 py-1.5 text-xs font-semibold text-[#0a0f24] hover:bg-amber-300 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs text-amber-100/50">Title</label>
          <input data-testid="entry-title" value={s.entry_title || ""} onChange={(e) => set("entry_title", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs text-amber-100/50">Meta (e.g. duration)</label>
          <input value={s.entry_meta || ""} onChange={(e) => set("entry_meta", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs text-amber-100/50">Button label</label>
          <input value={s.entry_cta || ""} onChange={(e) => set("entry_cta", e.target.value)} className={inputCls} />
        </div>
        <label className="flex items-center gap-2 self-end pb-2 text-sm text-amber-100/80">
          <input type="checkbox" checked={s.enabled !== false} onChange={(e) => set("enabled", e.target.checked)} className="h-4 w-4 accent-amber-400" />
          Show entry card
        </label>
      </div>

      <div className="mt-4 rounded-xl border border-amber-300/10 bg-[#080b16]/40 p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-amber-100/60">
          Image (shown in a container below the title)
        </p>
        <div className="flex items-center gap-3">
          <div className="grid h-20 w-32 shrink-0 place-items-center overflow-hidden rounded-lg border border-amber-300/10 bg-[#0d1226]">
            {s.entry_image ? (
              <img src={resolveMedia(s.entry_image)} alt={s.entry_image_alt || ""} className="h-full w-full object-cover" />
            ) : (
              <span className="text-xs text-amber-100/25">No image</span>
            )}
          </div>
          <div className="flex-1">
            <input ref={fileRef} data-testid="entry-image-input" type="file" accept="image/*" onChange={onImage} className="hidden" />
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => fileRef.current?.click()} disabled={imgBusy} className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300/25 px-3 py-1.5 text-xs text-amber-200 hover:bg-amber-400/10 disabled:opacity-50">
                {imgBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
                {s.entry_image ? "Replace" : "Upload"}
              </button>
              {s.entry_image && (
                <button onClick={() => set("entry_image", "")} className="inline-flex items-center gap-1.5 rounded-lg border border-red-400/20 px-3 py-1.5 text-xs text-red-300/80 hover:bg-red-500/10">
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              )}
            </div>
            <input value={s.entry_image_alt || ""} onChange={(e) => set("entry_image_alt", e.target.value)} placeholder="Alt text" className={`${inputCls} mt-2`} />
          </div>
        </div>
      </div>
    </div>
  );
};
